import {
  BadgeCheck,
  CalendarCheck,
  CheckCircle2,
  type LucideIcon,
  ShoppingBag,
} from "lucide-react";

export const MARISSA = {
  name: "Marissa Larimer",
  src: "/images/website/customers/marissa-larimer.jpg",
  joined: "Jun 12",
  brand: "Fitt Pilates",
} as const;

export { BRAND_LOGO } from "../../constants";

export type JourneyStepId = "intro" | "booked" | "visit" | "followup" | "offer" | "member";

export type JourneyStep = { id: JourneyStepId; label: string };

// Ordered milestones shown in the spotlight stepper. The thread drives which
// one is active as Marissa progresses from intro offer to member.
export const JOURNEY: JourneyStep[] = [
  { id: "intro", label: "Intro offer purchased" },
  { id: "booked", label: "Booked first class" },
  { id: "visit", label: "Attended first class" },
  { id: "followup", label: "Post-visit follow-up" },
  { id: "offer", label: "Return offer sent" },
  { id: "member", label: "Joined membership" },
];

export const JOURNEY_ORDER = JOURNEY.map((s) => s.id);

// The lifecycle status shown under Marissa's name — kept in sync between the
// thread header and the spotlight identity card. Derived from how far the
// journey has progressed so both surfaces always read the same state.
export type LeadStatus = "lead" | "attended" | "considering" | "member";

export const STATUS_LABEL: Record<LeadStatus, string> = {
  lead: "New lead",
  attended: "First class completed",
  considering: "Considering offer",
  member: "Member",
};

// Only the converted state gets the brand color; the rest stay muted.
export const STATUS_CLASS: Record<LeadStatus, string> = {
  lead: "text-muted-foreground",
  attended: "text-muted-foreground",
  considering: "text-muted-foreground",
  member: "text-primary dark:text-[hsl(var(--brand-accent))]",
};

export function statusFromIndex(activeIndex: number): LeadStatus {
  if (activeIndex >= JOURNEY_ORDER.indexOf("member")) return "member";
  if (activeIndex >= JOURNEY_ORDER.indexOf("offer")) return "considering";
  if (activeIndex >= JOURNEY_ORDER.indexOf("visit")) return "attended";
  return "lead";
}

export type Offer = { brand: string; title: string; detail: string; cta: string };

// One chronological thread combining SMS, customer events, autopilot explainers,
// an outgoing call and a customer voicemail. `delay` is how long each item holds
// as the latest before the next reveals. `step` advances the journey stepper.
export type ThreadItem =
  | {
      kind: "event";
      id: string;
      icon: LucideIcon;
      title: string;
      sub?: string;
      step?: JourneyStepId;
      delay: number;
    }
  | { kind: "system"; id: string; text: string; delay: number }
  | {
      kind: "message";
      id: string;
      direction: "incoming" | "outgoing";
      body: string;
      offer?: Offer;
      step?: JourneyStepId;
      delay: number;
    }
  | { kind: "call"; id: string; title: string; sub: string; delay: number }
  | { kind: "voicemail"; id: string; duration: string; transcript: string; delay: number };

export const TIMELINE: ThreadItem[] = [
  {
    kind: "event",
    id: "purchase",
    icon: ShoppingBag,
    title: "Purchased intro offer",
    sub: "First class free",
    step: "intro",
    delay: 1100,
  },
  { kind: "system", id: "wait-1d", text: "Waited 1 day", delay: 950 },
  {
    kind: "message",
    id: "nudge-1",
    direction: "outgoing",
    body: "Hi Marissa! So glad you grabbed your first free class at Fitt Pilates! Want me to get you booked in this week?",
    delay: 1900,
  },
  {
    kind: "system",
    id: "no-reply",
    text: "No reply. Nudged again after 3 days.",
    delay: 1200,
  },
  {
    kind: "message",
    id: "nudge-2",
    direction: "outgoing",
    body: "Still holding your spot! I can lock one in for you Saturday at 9am. Want me to grab it?",
    delay: 1900,
  },
  {
    kind: "message",
    id: "reply-book",
    direction: "incoming",
    body: "Sat 9am works, thank you!",
    delay: 1500,
  },
  {
    kind: "event",
    id: "booked",
    icon: CalendarCheck,
    title: "Booked first class",
    sub: "Sat · 9:00 AM with Mia",
    step: "booked",
    delay: 1300,
  },
  {
    kind: "event",
    id: "checkin",
    icon: CheckCircle2,
    title: "Checked in. First class attended.",
    step: "visit",
    delay: 1300,
  },
  { kind: "system", id: "wait-1h", text: "Waited 1 hour after class", delay: 950 },
  {
    kind: "message",
    id: "followup",
    direction: "outgoing",
    body: "You crushed it today! So great to see you. How'd your first class go, Marissa?",
    delay: 1800,
  },
  {
    kind: "message",
    id: "loved-it",
    direction: "incoming",
    body: "Loved it!! Mia was amazing 🙌",
    step: "followup",
    delay: 1500,
  },
  {
    kind: "system",
    id: "bring-back",
    text: "2 days later · Follow-up call",
    delay: 1200,
  },
  {
    kind: "call",
    id: "call",
    title: "Outgoing call",
    sub: "No answer. Left a friendly voicemail.",
    delay: 1700,
  },
  {
    kind: "voicemail",
    id: "voicemail",
    duration: "0:18",
    transcript:
      "Hey! So glad I finally tried Fitt Pilates. What class packages do you have? I'd love to keep coming.",
    delay: 2400,
  },
  { kind: "system", id: "replied", text: "Replied within minutes", delay: 950 },
  {
    kind: "message",
    id: "offer",
    direction: "outgoing",
    body: "Love that, Marissa! Here's 20% off your first month so you can keep the momentum going 🎉",
    offer: {
      brand: "Fitt Pilates",
      title: "20% off your first month",
      detail: "Membership · 4× per month",
      cta: "Claim offer",
    },
    step: "offer",
    delay: 2100,
  },
  {
    kind: "message",
    id: "signed-up",
    direction: "incoming",
    body: "Done! Just signed up 🎉",
    delay: 1500,
  },
  {
    kind: "event",
    id: "member",
    icon: BadgeCheck,
    title: "Joined membership · 4× per month",
    step: "member",
    delay: 1800,
  },
];
